import React, { useState } from 'react';
import type { CaptureSettings } from './types';
import { DEFAULT_CAPTURE_SETTINGS } from './lib/capture-settings';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { RotateCcw, Cpu, Grid3X3, Camera, Sparkles, Timer, Palette, ChevronDown, Target } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

/**
 * The Plumb Line Correction confidence. With no correction source there is nothing to be
 * confident about, so it reads "n/a" (the app stores 0 then, which read as "0%").
 */
export function floorConfidenceLabel(state?: { source?: string; confidence?: number }): string {
  if (!state?.source || state.source === 'none' || typeof state.confidence !== 'number') return 'n/a';
  return `${Math.round(state.confidence * 100)}%`;
}

interface CaptureSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  settings: CaptureSettings;
  onSettingsChange: (settings: CaptureSettings) => void;
  onReanalyze?: () => void;
  plumbToolState?: {
    source?: 'manual' | 'gyro' | 'landmarks' | 'opencv' | 'none';
    confidence?: number;
    warning?: 'sensor-disagreement';
  };
  onResetPlumbToAuto?: () => void;
  onStartManualPlumbAdjust?: () => void;
}

const SettingSection: React.FC<{ 
  title: string; 
  icon: React.ReactNode; 
  children: React.ReactNode;
  onReset?: () => void;
}> = ({ 
  title, 
  icon, 
  children,
  onReset
}) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 text-sm font-medium text-primary">
        {icon}
        {title}
      </div>
      {onReset && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          title={`Reset ${title} to defaults`}
        >
          <RotateCcw className="h-3 w-3 mr-1" />
          Reset
        </Button>
      )}
    </div>
    <div className="space-y-4 pl-1">
      {children}
    </div>
  </div>
);

const SettingRow: React.FC<{ label: string; description?: string; children: React.ReactNode }> = ({ 
  label, 
  description, 
  children 
}) => (
  <div className="flex items-center justify-between gap-4">
    <div className="flex-1 min-w-0">
      <Label className="text-sm font-normal text-foreground">{label}</Label>
      {description && (
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      )}
    </div>
    <div className="flex-shrink-0">
      {children}
    </div>
  </div>
);

const SliderRow: React.FC<{ 
  label: string; 
  description?: string;
  value: number; 
  min: number; 
  max: number; 
  step: number;
  displayValue?: string;
  onChange: (value: number) => void 
}> = ({ 
  label, 
  description,
  value, 
  min, 
  max, 
  step,
  displayValue,
  onChange 
}) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="text-sm font-normal">{label}</Label>
      <span className="text-xs text-muted-foreground tabular-nums">
        {displayValue ?? value}
      </span>
    </div>
    {description && (
      <p className="text-xs text-muted-foreground">{description}</p>
    )}
    <Slider
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={([v]) => onChange(v)}
      className="w-full"
    />
  </div>
);

const ColorRow: React.FC<{ 
  label: string; 
  description?: string;
  value: string; 
  onChange: (value: string) => void 
}> = ({ 
  label, 
  description,
  value, 
  onChange 
}) => (
  <div className="flex items-center justify-between gap-4">
    <div className="flex-1 min-w-0">
      <Label className="text-sm font-normal text-foreground">{label}</Label>
      {description && (
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      )}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <div 
        className="w-8 h-8 rounded border-2 border-border"
        style={{ backgroundColor: value }}
      />
      <Input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-8 p-0 border border-border rounded cursor-pointer"
      />
    </div>
  </div>
);

const CaptureSettingsPanel: React.FC<CaptureSettingsPanelProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange,
  onReanalyze,
  plumbToolState,
  onResetPlumbToAuto,
  onStartManualPlumbAdjust,
}) => {
  const updateSetting = <K extends keyof CaptureSettings>(key: K, value: CaptureSettings[K]) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const handleReset = () => {
    onSettingsChange({ ...DEFAULT_CAPTURE_SETTINGS });
  };

  // Reset functions for each category
  const resetImageEnhancement = () => {
    onSettingsChange({
      ...settings,
      autoBrightness: DEFAULT_CAPTURE_SETTINGS.autoBrightness,
      contrastBoost: DEFAULT_CAPTURE_SETTINGS.contrastBoost,
      sharpen: DEFAULT_CAPTURE_SETTINGS.sharpen,
    });
  };

  const resetDisplaySettings = () => {
    onSettingsChange({
      ...settings,
      landmarkSize: DEFAULT_CAPTURE_SETTINGS.landmarkSize,
      landmarkFillColor: DEFAULT_CAPTURE_SETTINGS.landmarkFillColor,
      landmarkStrokeColor: DEFAULT_CAPTURE_SETTINGS.landmarkStrokeColor,
      skeletonLineWidth: DEFAULT_CAPTURE_SETTINGS.skeletonLineWidth,
      skeletonLineColor: DEFAULT_CAPTURE_SETTINGS.skeletonLineColor,
      overlayLineWidth: DEFAULT_CAPTURE_SETTINGS.overlayLineWidth,
      overlayLineColor: DEFAULT_CAPTURE_SETTINGS.overlayLineColor,
      plumbLineColor: DEFAULT_CAPTURE_SETTINGS.plumbLineColor,
      gridLineColor: DEFAULT_CAPTURE_SETTINGS.gridLineColor,
      customLandmarkColor: DEFAULT_CAPTURE_SETTINGS.customLandmarkColor,
      pelvisLineColor: DEFAULT_CAPTURE_SETTINGS.pelvisLineColor,
      pelvisLineWidth: DEFAULT_CAPTURE_SETTINGS.pelvisLineWidth,
      showPelvisLine: DEFAULT_CAPTURE_SETTINGS.showPelvisLine,
      showPelvisLandmarks: DEFAULT_CAPTURE_SETTINGS.showPelvisLandmarks,
    });
  };

  const resetAIVision = () => {
    onSettingsChange({
      ...settings,
      modelQuality: DEFAULT_CAPTURE_SETTINGS.modelQuality,
      processingMode: DEFAULT_CAPTURE_SETTINGS.processingMode,
      visibilityThreshold: DEFAULT_CAPTURE_SETTINGS.visibilityThreshold,
      minPoseConfidence: DEFAULT_CAPTURE_SETTINGS.minPoseConfidence,
    });
  };

  const resetOverlaySettings = () => {
    onSettingsChange({
      ...settings,
      gridType: DEFAULT_CAPTURE_SETTINGS.gridType,
      gridOpacity: DEFAULT_CAPTURE_SETTINGS.gridOpacity,
      showGhostOutline: DEFAULT_CAPTURE_SETTINGS.showGhostOutline,
      ghostOpacity: DEFAULT_CAPTURE_SETTINGS.ghostOpacity,
      showLevelIndicator: DEFAULT_CAPTURE_SETTINGS.showLevelIndicator,
      showPlumbLine: DEFAULT_CAPTURE_SETTINGS.showPlumbLine,
      plumbLineStyle: DEFAULT_CAPTURE_SETTINGS.plumbLineStyle,
      enableAutoCapture: DEFAULT_CAPTURE_SETTINGS.enableAutoCapture,
      stabilityThreshold: DEFAULT_CAPTURE_SETTINGS.stabilityThreshold,
      holdDuration: DEFAULT_CAPTURE_SETTINGS.holdDuration,
      countdownDuration: DEFAULT_CAPTURE_SETTINGS.countdownDuration,
    });
  };

  const resetCameraSettings = () => {
    onSettingsChange({
      ...settings,
      preferredResolution: DEFAULT_CAPTURE_SETTINGS.preferredResolution,
      mirrorFrontCamera: DEFAULT_CAPTURE_SETTINGS.mirrorFrontCamera,
      defaultFacingMode: DEFAULT_CAPTURE_SETTINGS.defaultFacingMode,
    });
  };

  const resetImageSettings = () => {
    onSettingsChange({
      ...settings,
      autoBrightness: DEFAULT_CAPTURE_SETTINGS.autoBrightness,
      contrastBoost: DEFAULT_CAPTURE_SETTINGS.contrastBoost,
      sharpen: DEFAULT_CAPTURE_SETTINGS.sharpen,
      rotation: DEFAULT_CAPTURE_SETTINGS.rotation,
      perspectiveX: DEFAULT_CAPTURE_SETTINGS.perspectiveX,
      perspectiveY: DEFAULT_CAPTURE_SETTINGS.perspectiveY,
      tilt: DEFAULT_CAPTURE_SETTINGS.tilt,
      yaw: DEFAULT_CAPTURE_SETTINGS.yaw,
      zoom: DEFAULT_CAPTURE_SETTINGS.zoom,
      landmarkSize: DEFAULT_CAPTURE_SETTINGS.landmarkSize,
      landmarkFillColor: DEFAULT_CAPTURE_SETTINGS.landmarkFillColor,
      landmarkStrokeColor: DEFAULT_CAPTURE_SETTINGS.landmarkStrokeColor,
      skeletonLineWidth: DEFAULT_CAPTURE_SETTINGS.skeletonLineWidth,
      skeletonLineColor: DEFAULT_CAPTURE_SETTINGS.skeletonLineColor,
      overlayLineWidth: DEFAULT_CAPTURE_SETTINGS.overlayLineWidth,
      overlayLineColor: DEFAULT_CAPTURE_SETTINGS.overlayLineColor,
      plumbLineColor: DEFAULT_CAPTURE_SETTINGS.plumbLineColor,
      gridLineColor: DEFAULT_CAPTURE_SETTINGS.gridLineColor,
      customLandmarkColor: DEFAULT_CAPTURE_SETTINGS.customLandmarkColor,
      pelvisLineColor: DEFAULT_CAPTURE_SETTINGS.pelvisLineColor,
      pelvisLineWidth: DEFAULT_CAPTURE_SETTINGS.pelvisLineWidth,
      showPelvisLine: DEFAULT_CAPTURE_SETTINGS.showPelvisLine,
      showPelvisLandmarks: DEFAULT_CAPTURE_SETTINGS.showPelvisLandmarks,
    });
  };

  const resetPelvisOverlaySettings = () => {
    onSettingsChange({
      ...settings,
      customLandmarkColor: DEFAULT_CAPTURE_SETTINGS.customLandmarkColor,
      pelvisLineColor: DEFAULT_CAPTURE_SETTINGS.pelvisLineColor,
      pelvisLineWidth: DEFAULT_CAPTURE_SETTINGS.pelvisLineWidth,
      showPelvisLine: DEFAULT_CAPTURE_SETTINGS.showPelvisLine,
      showPelvisLandmarks: DEFAULT_CAPTURE_SETTINGS.showPelvisLandmarks,
    });
  };

  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        overlayClassName="bg-black/25"
        className="w-[400px] sm:w-[450px] overflow-y-auto bg-background/95 backdrop-blur-xl border-border/50"
      >
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <Cpu className="h-5 w-5 text-primary" />
            Capture Settings
          </SheetTitle>
          <SheetDescription>
            Configure AI vision, overlays, and camera settings
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {/* Accordion for Overlay and Camera (mutually exclusive) */}
          <Accordion type="single" collapsible defaultValue="overlay" className="space-y-4">
            {/* Overlay Settings */}
            <AccordionItem value="overlay" className="border-none">
              <div className="space-y-4">
                <AccordionTrigger className="p-0 hover:no-underline">
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Grid3X3 className="h-4 w-4" />
                    Overlay Settings
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0">
                  <div className="space-y-4 pl-1">
                    {/* Overlays & Grid */}
                    <SettingSection 
                      title="Overlays & Grid" 
                      icon={<Grid3X3 className="h-4 w-4" />}
                      onReset={() => {
                        onSettingsChange({
                          ...settings,
                          gridType: DEFAULT_CAPTURE_SETTINGS.gridType,
                          gridOpacity: DEFAULT_CAPTURE_SETTINGS.gridOpacity,
                          showGhostOutline: DEFAULT_CAPTURE_SETTINGS.showGhostOutline,
                          ghostOpacity: DEFAULT_CAPTURE_SETTINGS.ghostOpacity,
                          showLevelIndicator: DEFAULT_CAPTURE_SETTINGS.showLevelIndicator,
                          showPlumbLine: DEFAULT_CAPTURE_SETTINGS.showPlumbLine,
                          plumbLineStyle: DEFAULT_CAPTURE_SETTINGS.plumbLineStyle,
                        });
                      }}
                    >
            <SettingRow label="Grid Type">
              <Select
                value={settings.gridType}
                onValueChange={(v) => updateSetting('gridType', v as CaptureSettings['gridType'])}
              >
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3x3">3×3</SelectItem>
                  <SelectItem value="golden">Golden</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SliderRow
              label="Grid Opacity"
              value={settings.gridOpacity}
              min={0.1}
              max={1.0}
              step={0.1}
              displayValue={`${Math.round(settings.gridOpacity * 100)}%`}
              onChange={(v) => updateSetting('gridOpacity', v)}
            />

            <SettingRow label="Ghost Outline" description="Display body silhouette guide">
              <Switch
                checked={settings.showGhostOutline}
                onCheckedChange={(v) => updateSetting('showGhostOutline', v)}
              />
            </SettingRow>

            {settings.showGhostOutline && (
              <SliderRow
                label="Ghost Opacity"
                value={settings.ghostOpacity}
                min={0.1}
                max={1.0}
                step={0.1}
                displayValue={`${Math.round(settings.ghostOpacity * 100)}%`}
                onChange={(v) => updateSetting('ghostOpacity', v)}
              />
            )}

            <SettingRow label="Level Indicator" description="Display device level bubble">
              <Switch
                checked={settings.showLevelIndicator}
                onCheckedChange={(v) => updateSetting('showLevelIndicator', v)}
              />
            </SettingRow>

            <SettingRow label="Plumb Line" description="Display vertical reference line">
              <Switch
                checked={settings.showPlumbLine}
                onCheckedChange={(v) => updateSetting('showPlumbLine', v)}
              />
            </SettingRow>

            {settings.showPlumbLine && (
              <SettingRow label="Line Style">
                <Select
                  value={settings.plumbLineStyle}
                  onValueChange={(v) => updateSetting('plumbLineStyle', v as CaptureSettings['plumbLineStyle'])}
                >
                  <SelectTrigger className="w-[100px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solid">Solid</SelectItem>
                    <SelectItem value="dashed">Dashed</SelectItem>
                    <SelectItem value="dotted">Dotted</SelectItem>
                  </SelectContent>
                </Select>
              </SettingRow>
            )}

            <SettingRow
              label="Landmark snap"
              description="Snap landmarks & ASIS/PSIS pins to their detected position"
            >
              <div className="flex items-center gap-2">
                {settings.landmarkSnap && (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={settings.landmarkSnapRadius ?? DEFAULT_CAPTURE_SETTINGS.landmarkSnapRadius}
                      onChange={(e) =>
                        updateSetting(
                          'landmarkSnapRadius',
                          Math.max(1, Math.min(100, Number(e.target.value) || 1))
                        )
                      }
                      className="w-16 h-8"
                      title="Snap radius in pixels"
                    />
                    <span className="text-xs text-muted-foreground">px</span>
                  </div>
                )}
                <Switch
                  checked={settings.landmarkSnap}
                  onCheckedChange={(v) => updateSetting('landmarkSnap', v)}
                />
              </div>
            </SettingRow>

            <SettingRow
              label="Plumbline snap"
              description="Snap the plumb line back to vertical"
            >
              <div className="flex items-center gap-2">
                {settings.plumblineSnap && (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={settings.plumblineSnapRadius ?? DEFAULT_CAPTURE_SETTINGS.plumblineSnapRadius}
                      onChange={(e) =>
                        updateSetting(
                          'plumblineSnapRadius',
                          Math.max(1, Math.min(100, Number(e.target.value) || 1))
                        )
                      }
                      className="w-16 h-8"
                      title="Snap radius in pixels"
                    />
                    <span className="text-xs text-muted-foreground">px</span>
                  </div>
                )}
                <Switch
                  checked={settings.plumblineSnap}
                  onCheckedChange={(v) => updateSetting('plumblineSnap', v)}
                />
              </div>
            </SettingRow>

            <SettingRow label="Toolbar Position" description="Position of the floating control bar">
              <Select
                value={settings.toolbarPosition}
                onValueChange={(v) => updateSetting('toolbarPosition', v as CaptureSettings['toolbarPosition'])}
              >
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bottom">Bottom</SelectItem>
                  <SelectItem value="top">Top</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>
          </SettingSection>

                    {/* Display Settings */}
                    <SettingSection 
                      title="Display Settings" 
                      icon={<Palette className="h-4 w-4" />}
                      onReset={resetDisplaySettings}
                    >
                      <SliderRow
                        label="Landmark Size"
                        description="Size multiplier for landmark points"
                        value={settings.landmarkSize}
                        min={0.5}
                        max={3.5}
                        step={0.05}
                        displayValue={`${settings.landmarkSize.toFixed(2)}x`}
                        onChange={(v) => updateSetting('landmarkSize', v)}
                      />

                      <ColorRow
                        label="Landmark Fill Color"
                        description="Fill color for landmark points"
                        value={settings.landmarkFillColor}
                        onChange={(v) => updateSetting('landmarkFillColor', v)}
                      />

                      <ColorRow
                        label="Landmark Stroke Color"
                        description="Border color for landmark points"
                        value={settings.landmarkStrokeColor}
                        onChange={(v) => updateSetting('landmarkStrokeColor', v)}
                      />

                      <SliderRow
                        label="Skeleton Line Width"
                        description="Width multiplier for skeleton lines"
                        value={settings.skeletonLineWidth}
                        min={0.5}
                        max={3.5}
                        step={0.05}
                        displayValue={`${settings.skeletonLineWidth.toFixed(2)}x`}
                        onChange={(v) => updateSetting('skeletonLineWidth', v)}
                      />

                      <ColorRow
                        label="Skeleton Line Color"
                        description="Color for skeleton connecting lines"
                        value={settings.skeletonLineColor}
                        onChange={(v) => updateSetting('skeletonLineColor', v)}
                      />

                      <SliderRow
                        label="Overlay Line Width"
                        description="Width multiplier for overlay lines"
                        value={settings.overlayLineWidth}
                        min={0.5}
                        max={2.0}
                        step={0.1}
                        displayValue={`${settings.overlayLineWidth.toFixed(1)}x`}
                        onChange={(v) => updateSetting('overlayLineWidth', v)}
                      />

                      <ColorRow
                        label="Overlay Line Color"
                        description="Color for overlay reference lines"
                        value={settings.overlayLineColor}
                        onChange={(v) => updateSetting('overlayLineColor', v)}
                      />

                      <ColorRow
                        label="Plumb Line Color"
                        description="Color for vertical plumb reference line"
                        value={settings.plumbLineColor}
                        onChange={(v) => updateSetting('plumbLineColor', v)}
                      />

                      <ColorRow
                        label="Grid Line Color"
                        description="Color for grid overlay lines"
                        value={settings.gridLineColor}
                        onChange={(v) => updateSetting('gridLineColor', v)}
                      />
                    </SettingSection>

                    <SettingSection
                      title="ASIS–PSIS (pelvis)"
                      icon={<Target className="h-4 w-4" />}
                      onReset={resetPelvisOverlaySettings}
                    >
                      <SettingRow
                        label="Show ASIS–PSIS line"
                        description="Connecting segment between ASIS and PSIS positions"
                      >
                        <Switch
                          checked={settings.showPelvisLine}
                          onCheckedChange={(v) => updateSetting('showPelvisLine', v)}
                        />
                      </SettingRow>

                      <SettingRow
                        label="Show ASIS / PSIS landmarks"
                        description="Pin markers for pelvis points (line can stay on when pins are hidden)"
                      >
                        <Switch
                          checked={settings.showPelvisLandmarks}
                          onCheckedChange={(v) => updateSetting('showPelvisLandmarks', v)}
                        />
                      </SettingRow>

                      <ColorRow
                        label="ASIS / PSIS landmark color"
                        description="Fill and hover ring for pelvis pin markers"
                        value={settings.customLandmarkColor}
                        onChange={(v) => updateSetting('customLandmarkColor', v)}
                      />

                      <ColorRow
                        label="Pelvis line color"
                        description="Color of the ASIS–PSIS segment on each view"
                        value={settings.pelvisLineColor}
                        onChange={(v) => updateSetting('pelvisLineColor', v)}
                      />

                      <SliderRow
                        label="Pelvis line thickness"
                        description="Width multiplier for the ASIS–PSIS line only"
                        value={settings.pelvisLineWidth}
                        min={0.5}
                        max={3.5}
                        step={0.05}
                        displayValue={`${settings.pelvisLineWidth.toFixed(2)}x`}
                        onChange={(v) => updateSetting('pelvisLineWidth', v)}
                      />
                    </SettingSection>
                  </div>
                </AccordionContent>
              </div>
            </AccordionItem>

            {/* Camera Settings */}
            <AccordionItem value="camera" className="border-none">
              <div className="space-y-4">
                <AccordionTrigger className="p-0 hover:no-underline">
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Camera className="h-4 w-4" />
                    Camera Settings
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0">
                  <div className="space-y-4 pl-1">
                    {/* Auto-Capture */}
                    <SettingSection 
                      title="Auto-Capture" 
                      icon={<Timer className="h-4 w-4" />}
                      onReset={() => {
                        onSettingsChange({
                          ...settings,
                          enableAutoCapture: DEFAULT_CAPTURE_SETTINGS.enableAutoCapture,
                          stabilityThreshold: DEFAULT_CAPTURE_SETTINGS.stabilityThreshold,
                          holdDuration: DEFAULT_CAPTURE_SETTINGS.holdDuration,
                          countdownDuration: DEFAULT_CAPTURE_SETTINGS.countdownDuration,
                        });
                      }}
                    >
                      <SettingRow label="Enable Auto-Capture" description="Auto-capture when pose is stable">
                        <Switch
                          checked={settings.enableAutoCapture}
                          onCheckedChange={(v) => updateSetting('enableAutoCapture', v)}
                        />
                      </SettingRow>

                      {settings.enableAutoCapture && (
                        <>
                          <SliderRow
                            label="Stability Threshold"
                            description="Max movement (px) for 'stable'"
                            value={settings.stabilityThreshold}
                            min={5}
                            max={50}
                            step={5}
                            displayValue={`${settings.stabilityThreshold}px`}
                            onChange={(v) => updateSetting('stabilityThreshold', v)}
                          />

                          <SliderRow
                            label="Hold Duration"
                            description="Seconds to hold before capture"
                            value={settings.holdDuration}
                            min={1}
                            max={5}
                            step={0.5}
                            displayValue={`${settings.holdDuration}s`}
                            onChange={(v) => updateSetting('holdDuration', v)}
                          />

                          <SliderRow
                            label="Countdown Duration"
                            description="Visual countdown before snap"
                            value={settings.countdownDuration}
                            min={1}
                            max={10}
                            step={1}
                            displayValue={`${settings.countdownDuration}s`}
                            onChange={(v) => updateSetting('countdownDuration', v)}
                          />
                        </>
                      )}
                    </SettingSection>

                    {/* Camera */}
                    <SettingSection 
                      title="Camera" 
                      icon={<Camera className="h-4 w-4" />}
                      onReset={resetCameraSettings}
                    >
            <SettingRow label="Preferred Resolution">
              <Select
                value={settings.preferredResolution}
                onValueChange={(v) => updateSetting('preferredResolution', v as CaptureSettings['preferredResolution'])}
              >
                <SelectTrigger className="w-[120px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="640x480">640×480</SelectItem>
                  <SelectItem value="1280x720">1280×720</SelectItem>
                  <SelectItem value="1920x1080">1920×1080</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow label="Default Camera">
              <Select
                value={settings.defaultFacingMode}
                onValueChange={(v) => updateSetting('defaultFacingMode', v as CaptureSettings['defaultFacingMode'])}
              >
                <SelectTrigger className="w-[120px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Front</SelectItem>
                  <SelectItem value="environment">Rear</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow label="Mirror Front Camera" description="Flip horizontal for selfie mode">
              <Switch
                checked={settings.mirrorFrontCamera}
                onCheckedChange={(v) => updateSetting('mirrorFrontCamera', v)}
              />
                      </SettingRow>
                    </SettingSection>
                  </div>
                </AccordionContent>
              </div>
            </AccordionItem>
          </Accordion>

          {/* Image Settings - Collapsible */}
          <Collapsible open={isImageOpen} onOpenChange={setIsImageOpen}>
            <div className="space-y-4">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center justify-between w-full p-0 h-auto hover:bg-transparent"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Sparkles className="h-4 w-4" />
                    Image Settings
                  </div>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isImageOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <div className="space-y-4 pl-1">
                  {/* Image Enhancement */}
                  <SettingSection 
            title="Image Enhancement" 
            icon={<Sparkles className="h-4 w-4" />}
            onReset={resetImageEnhancement}
          >
            <SettingRow label="Auto Brightness" description="Auto-adjust image brightness">
              <Switch
                checked={settings.autoBrightness}
                onCheckedChange={(v) => updateSetting('autoBrightness', v)}
              />
            </SettingRow>

            <SliderRow
              label="Contrast Boost"
              value={settings.contrastBoost}
              min={-1}
              max={1}
              step={0.1}
              displayValue={settings.contrastBoost > 0 ? `+${settings.contrastBoost.toFixed(1)}` : settings.contrastBoost.toFixed(1)}
              onChange={(v) => updateSetting('contrastBoost', v)}
            />

            <SettingRow label="Sharpen" description="Apply sharpening filter">
              <Switch
                checked={settings.sharpen}
                onCheckedChange={(v) => updateSetting('sharpen', v)}
              />
                    </SettingRow>
                  </SettingSection>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Advanced Settings - AI Vision */}
          <Collapsible open={isAdvancedOpen} onOpenChange={setIsAdvancedOpen}>
            <div className="space-y-4">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center justify-between w-full p-0 h-auto hover:bg-transparent"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Cpu className="h-4 w-4" />
                    Advanced Settings
                  </div>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <div className="space-y-4 pl-1">
                  <SettingSection 
                    title="AI Vision" 
                    icon={<Cpu className="h-4 w-4" />}
                    onReset={resetAIVision}
                  >
                    <div className="text-xs text-amber-500/80 bg-amber-500/10 px-2 py-1.5 rounded mb-2">
                      ⚠️ Model & Processing changes require page reload
                    </div>
                    
                    <SettingRow label="Model Quality" description="Full is more accurate, Lite is faster">
                      <Select
                        value={settings.modelQuality}
                        onValueChange={(v) => updateSetting('modelQuality', v as CaptureSettings['modelQuality'])}
                      >
                        <SelectTrigger className="w-[100px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">Full</SelectItem>
                          <SelectItem value="lite">Lite</SelectItem>
                        </SelectContent>
                      </Select>
                    </SettingRow>

                    <SettingRow label="Processing Mode" description="GPU is faster but may fail on some devices">
                      <Select
                        value={settings.processingMode}
                        onValueChange={(v) => updateSetting('processingMode', v as CaptureSettings['processingMode'])}
                      >
                        <SelectTrigger className="w-[100px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="GPU">GPU</SelectItem>
                          <SelectItem value="CPU">CPU</SelectItem>
                        </SelectContent>
                      </Select>
                    </SettingRow>

                    <SliderRow
                      label="Visibility Threshold"
                      description="Minimum confidence to show landmark"
                      value={settings.visibilityThreshold}
                      min={0.1}
                      max={0.9}
                      step={0.05}
                      displayValue={`${Math.round(settings.visibilityThreshold * 100)}%`}
                      onChange={(v) => updateSetting('visibilityThreshold', v)}
                    />

                    <SliderRow
                      label="Pose Confidence"
                      description="Minimum confidence to detect a pose"
                      value={settings.minPoseConfidence}
                      min={0.1}
                      max={0.9}
                      step={0.05}
                      displayValue={`${Math.round(settings.minPoseConfidence * 100)}%`}
                      onChange={(v) => updateSetting('minPoseConfidence', v)}
                    />
                  </SettingSection>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          <Separator className="bg-border/50" />

          <SettingSection
            title="Plumb Line Correction"
            icon={<Grid3X3 className="h-4 w-4" />}
          >
            <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground space-y-1">
              <p>
                Source: <span className="font-medium text-foreground">{plumbToolState?.source ?? 'none'}</span>
              </p>
              <p>
                Confidence:{' '}
                <span className="font-medium text-foreground">
                  {floorConfidenceLabel(plumbToolState)}
                </span>
              </p>
              {plumbToolState?.warning === 'sensor-disagreement' && (
                <p className="text-amber-400">
                  Warning: Gyro and landmark correction disagree.
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button
                variant="secondary"
                className="w-full"
                onClick={onStartManualPlumbAdjust}
                disabled={!onStartManualPlumbAdjust}
              >
                Set Floor Manually
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={onResetPlumbToAuto}
                disabled={!onResetPlumbToAuto}
              >
                Reset to Auto
              </Button>
            </div>
          </SettingSection>

          <Separator className="bg-border/50" />

          {/* Actions */}
          <div className="space-y-2">
            {onReanalyze && (
              <Button
                variant="secondary"
                className="w-full bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30"
                onClick={onReanalyze}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Reanalyze Current Images
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleReset}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CaptureSettingsPanel;

